import React from "react";
import {
  AlertOctagon, AlertTriangle, Info, CheckCircle2, Clock,
  ShieldCheck, EyeOff, Archive,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const SEVERITY_STYLES = {
  Critical: "bg-rose-50 text-rose-800 border-rose-200",
  High: "bg-amber-50 text-amber-800 border-amber-200",
  Medium: "bg-sky-50 text-sky-800 border-sky-200",
  Low: "bg-zinc-50 text-zinc-700 border-zinc-200",
};
const SEVERITY_ICONS = {
  Critical: AlertOctagon,
  High: AlertTriangle,
  Medium: Info,
  Low: Info,
};

const STATUS_STYLES = {
  active: "bg-rose-50 text-rose-800 border-rose-200",
  snoozed: "bg-zinc-100 text-zinc-700 border-zinc-300",
  accepted_risk: "bg-violet-50 text-violet-800 border-violet-200",
  muted: "bg-zinc-50 text-zinc-500 border-zinc-200 line-through decoration-zinc-400",
  resolved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  superseded: "bg-zinc-50 text-zinc-400 border-zinc-200",
};
const STATUS_ICONS = {
  active: AlertOctagon,
  snoozed: Clock,
  accepted_risk: ShieldCheck,
  muted: EyeOff,
  resolved: CheckCircle2,
  superseded: Archive,
};
const STATUS_LABELS = {
  active: "Active",
  snoozed: "Snoozed",
  accepted_risk: "Accepted risk",
  muted: "Muted",
  resolved: "Resolved",
  superseded: "Superseded",
};

export function AlertSeverityBadge({ severity, className = "", testId }) {
  const { t } = useTranslation("alerts");
  const Icon = SEVERITY_ICONS[severity] || Info;
  const cls = SEVERITY_STYLES[severity] || SEVERITY_STYLES.Low;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${cls} ${className}`}
      data-testid={testId || `alert-severity-${severity}`}
    >
      <Icon size={11} strokeWidth={2} />
      {t(`severity.${severity}`, severity)}
    </span>
  );
}

export function AlertStateBadge({ status, className = "", testId }) {
  const { t } = useTranslation("alerts");
  const Icon = STATUS_ICONS[status] || Info;
  const cls = STATUS_STYLES[status] || STATUS_STYLES.active;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${cls} ${className}`}
      data-testid={testId || `alert-state-${status}`}
    >
      <Icon size={11} strokeWidth={2} />
      {t(`status.${status}`, STATUS_LABELS[status] || status)}
    </span>
  );
}

const SOURCE_LABELS = {
  finding: "Finding",
  recommendation: "Recommendation",
  deadline: "Deadline",
  covenant_breach: "Covenant breach",
  manual: "Manual",
  clause_risk: "Risky clause",
};
export function AlertSourceBadge({ source_type, testId }) {
  const { t } = useTranslation("alerts");
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-600 font-mono"
      data-testid={testId || `alert-source-${source_type}`}
    >
      {t(`source.${source_type}`, SOURCE_LABELS[source_type] || source_type)}
    </span>
  );
}

/**
 * 0–100 horizontal urgency meter with color-graded fill.
 * 0–30 zinc · 30–60 amber · 60–80 orange · 80–100 rose
 */
export function UrgencyMeter({ score, signals, compact = false, testId }) {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  const tone =
    v >= 80 ? "bg-rose-600" : v >= 60 ? "bg-orange-500" :
    v >= 30 ? "bg-amber-500" : "bg-zinc-400";

  if (compact) {
    return (
      <div
        className="inline-flex items-center gap-1.5"
        title={signals ? `severity ${signals.severity_weight} · temporal ${signals.temporal_weight} · financial ${signals.financial_weight} · blocking ${signals.blocking_weight}` : undefined}
        data-testid={testId || "urgency-meter-compact"}
      >
        <div className="w-12 h-1.5 rounded-full bg-zinc-200 overflow-hidden">
          <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
        </div>
        <span className="font-mono text-xs tabular-nums text-zinc-700 w-7 text-right">{v}</span>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid={testId || "urgency-meter"}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Urgency</span>
        <span className="font-mono text-sm tabular-nums font-semibold text-zinc-950">{v}<span className="text-zinc-400 text-xs">/100</span></span>
      </div>
      <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${v}%` }} />
      </div>
      {signals && (
        <div className="grid grid-cols-4 gap-1 mt-1.5">
          {[
            ["Severity", signals.severity_weight, 40],
            ["Temporal", signals.temporal_weight, 30],
            ["Financial", signals.financial_weight, 20],
            ["Blocking", signals.blocking_weight, 10],
          ].map(([label, w, max]) => (
            <div key={label} className="text-[10px]" data-testid={`urgency-signal-${label.toLowerCase()}`}>
              <p className="text-zinc-500">{label}</p>
              <p className="font-mono tabular-nums text-zinc-800">
                {((w || 0) * max).toFixed(1)}<span className="text-zinc-400">/{max}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { SEVERITY_STYLES, STATUS_LABELS };
